/**
 * Frame capture from a MediaStreamTrack (TI-739b).
 *
 * Owns a hidden <video> fed by the candidate's camera track plus a scratch <canvas>.
 * Cross-browser (no ImageCapture dependency) and fail-open — any failure returns null
 * rather than throwing, so the live room is never disrupted by the integrity monitor.
 */

export function createFrameGrabber(mediaStreamTrack) {
  let video = null;
  let canvas = null;

  try {
    video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.srcObject = new MediaStream([mediaStreamTrack]);
    // Best-effort play; some browsers resolve only after metadata.
    const p = video.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    canvas = document.createElement("canvas");
  } catch (_) {
    video = null;
    canvas = null;
  }

  function isReady() {
    return !!video && video.readyState >= 2 && video.videoWidth > 0;
  }

  return {
    get videoElement() {
      return video;
    },
    isReady,
    async grabBlob(quality = 0.7, maxWidth = 640) {
      if (!isReady() || !canvas) return null;
      try {
        const scale = Math.min(1, maxWidth / video.videoWidth);
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        return await new Promise((resolve) => {
          canvas.toBlob((b) => resolve(b), "image/jpeg", quality);
        });
      } catch (_) {
        return null;
      }
    },
    dispose() {
      try {
        if (video) {
          video.srcObject = null;
          video.remove();
        }
      } catch (_) {
        /* best-effort */
      }
      video = null;
      canvas = null;
    },
  };
}
